import { NavTo } from "../routing-types";
import { isWatchlistItemPathname, type WatchlistItemPathname } from "./paths";

export interface CreateWatchFormDefaults {
	description: string;
	location: string;
	title: string;
}

/**
 * `location.state` for `/watchlist/create` when the user is creating a new
 * watch from an existing watch's description edit flow.
 */
export type CreateWatchFromWatchRouterState = {
	defaults: CreateWatchFormDefaults;
	kind: NavTo<"create-watch">;
	origin: CreateWatchOrigin;
};

export type CreateWatchOrigin = WatchlistItemPathname;

export function createCreateWatchFromWatchRouterState(
	defaults: CreateWatchFormDefaults,
	origin: CreateWatchOrigin,
): CreateWatchFromWatchRouterState {
	return { defaults, kind: "to:create-watch", origin };
}

/**
 * Attach router state when navigating to create-watch from a watch detail page
 * with prefilled form defaults. No state when defaults are omitted or the
 * current page is not a watch detail route.
 */
export function getCreateWatchRouterStateFromPathname(
	pathname: string,
	defaults?: CreateWatchFormDefaults,
): CreateWatchFromWatchRouterState | undefined {
	if (!defaults || !isWatchlistItemPathname(pathname)) return undefined;
	return createCreateWatchFromWatchRouterState(defaults, pathname);
}

export function parseCreateWatchFromWatchRouterState(
	state: unknown,
): CreateWatchFromWatchRouterState | undefined {
	if (!state || typeof state !== "object") return undefined;

	const record = state as Record<string, unknown>;
	if (record.kind !== "to:create-watch") return undefined;
	if (!isCreateWatchFormDefaults(record.defaults)) return undefined;
	if (!isWatchlistItemPathname(record.origin)) return undefined;

	return createCreateWatchFromWatchRouterState(record.defaults, record.origin);
}

function isCreateWatchFormDefaults(
	value: unknown,
): value is CreateWatchFormDefaults {
	if (!value || typeof value !== "object") return false;

	const record = value as Record<string, unknown>;
	return (
		typeof record.description === "string" &&
		typeof record.location === "string" &&
		typeof record.title === "string"
	);
}
